import {
  useContext,
  createContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import type { Restaurant } from 'hooks/use-unit';
import { api } from 'services/api';
import { useRouter } from 'next/router';
import { useAuth } from 'hooks/use-auth';
import { Event } from 'utils/helpers';
import { getLines } from 'hooks/use-lines';

type SettingsProps = {
  pricing: number;
  table_people: number[];
  tables_in_line: number;
};

export type LineContextProps = {
  unit?: Restaurant | null;
  quantity?: string | null;
  settings?: SettingsProps;
  setQuantity: (quantity: string | null) => void;
  handleSetUnit: (unit: Restaurant | null) => void;
  handleClickInButtonGetInLine: (unit: Restaurant | null) => void;
  isUserInLineModalVisible: boolean;
  setIsUserInLineModalVisible: (value: boolean) => void;
  getIsUserInLine: () => Promise<boolean>;
  isFetchingUserLineStatus: boolean;
};

export const LineContextDefaultValues = {
  handleSetUnit: () => null,
  setQuantity: () => null,
  handleClickInButtonGetInLine: () => null,
  isUserInLineModalVisible: false,
  setIsUserInLineModalVisible: () => null,
  getIsUserInLine: async () => false,
  isFetchingUserLineStatus: false,
};

export const LineContext = createContext<LineContextProps>(
  LineContextDefaultValues
);

export type LineProviderProps = {
  children: React.ReactNode;
};

type GetSettingsProps = {
  status: number;
  data?: SettingsProps;
};

export const getSettings = async (id: string): Promise<GetSettingsProps> => {
  try {
    const { status, data } = await api.get(`line/v1/units/${id}/settings`);

    return {
      status,
      data: data.data,
    };
  } catch (error) {
    return {
      status: 400,
    };
  }
};

const LineProvider = ({ children }: LineProviderProps) => {
  const router = useRouter();
  const { user, handleIsOpenLoginModal } = useAuth();
  const [unit, setUnit] = useState<Restaurant | null>();
  const [quantity, setQuantity] = useState<string | null>();
  const [settings, setSettings] = useState<SettingsProps>();
  const [isFetchingUserLineStatus, setIsFetchingUserLineStatus] =
    useState(false);
  const [isUserInLineModalVisible, setIsUserInLineModalVisible] =
    useState(false);

  const getIsUserInLine = async (): Promise<boolean> => {
    try {
      setIsFetchingUserLineStatus(true);

      const { data } = await getLines({
        status: 'confirmed',
        sort: 'created_at:DESC',
        per_page: 1,
      });

      setIsFetchingUserLineStatus(false);

      if (data.length) {
        return true;
      }

      return false;
    } catch (error) {
      setIsFetchingUserLineStatus(false);
      return false;
    }
  };

  const handleSetUnit = (unit: Restaurant | null) => setUnit(unit);

  const handleSettings = useCallback(async () => {
    if (!unit) return;

    const { status, data } = await getSettings(unit.id);

    if (status === 200) setSettings(data);
    else {
      setUnit(null);
      router.push('/my-position');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit]);

  useEffect(() => {
    if (unit) {
      handleSettings();
    }
  }, [unit, handleSettings]);

  const handleClickInButtonGetInLine = async (unit: Restaurant | null) => {
    if (user) {
      const isInLine = await getIsUserInLine();

      if (isInLine) {
        setIsUserInLineModalVisible(true);

        return;
      }

      handleSetUnit(unit);

      Event.push('getin_home_remoteline_enter_click', {
        diner_id: user?.id,
        unit_id: unit?.id,
        unit_slug: unit?.slug,
      });
    } else {
      handleIsOpenLoginModal(true);
    }
  };

  return (
    <LineContext.Provider
      value={{
        unit,
        quantity,
        settings,
        setQuantity,
        handleSetUnit,
        handleClickInButtonGetInLine,
        isUserInLineModalVisible,
        setIsUserInLineModalVisible,
        getIsUserInLine,
        isFetchingUserLineStatus,
      }}
    >
      {children}
    </LineContext.Provider>
  );
};

const useLine = () => useContext(LineContext);

export { LineProvider, useLine };
